import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Palette, RefreshCw, Save, Eye, Upload } from 'lucide-react';
import { API } from '@/App';

const ThemeManagementPage = () => {
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState({
    primary_color: '#0891b2',
    secondary_color: '#06b6d4',
    accent_color: '#0e7490',
    logo_url: '/SFOALC-logo.png',
    logo_white_url: '/SFOALC-blanc.png',
    favicon_url: '/favicon.ico',
    site_name: 'SFOALC',
    site_description: 'Société Française des Ophtalmologistes Adaptateurs de Lentilles de Contact'
  });
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      const response = await axios.get(`${API}/theme`);
      setTheme(response.data);
    } catch (error) {
      console.error('Error fetching theme:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/theme`, theme);
      toast.success('Thème mis à jour avec succès !');
      
      // Apply theme changes to document
      applyThemeToDocument(theme);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du thème');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser le thème aux valeurs par défaut ?')) {
      setLoading(true);
      try {
        const response = await axios.post(`${API}/theme/reset`);
        setTheme(response.data.theme);
        toast.success('Thème réinitialisé !');
        
        // Apply default theme
        applyThemeToDocument(response.data.theme);
      } catch (error) {
        toast.error('Erreur lors de la réinitialisation');
      } finally {
        setLoading(false);
      }
    }
  };

  const applyThemeToDocument = (themeData) => {
    // Update CSS custom properties
    document.documentElement.style.setProperty('--color-primary', themeData.primary_color);
    document.documentElement.style.setProperty('--color-secondary', themeData.secondary_color);
    document.documentElement.style.setProperty('--color-accent', themeData.accent_color);
    
    // Update favicon
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) {
      favicon.href = themeData.favicon_url;
    }
    
    // Update page title
    document.title = themeData.site_name;
  };

  const handleChange = (field, value) => {
    setTheme({ ...theme, [field]: value });
  };

  const togglePreview = () => {
    if (!previewMode) {
      applyThemeToDocument(theme);
    }
    setPreviewMode(!previewMode);
  };

  const colorPresets = [
    { name: 'Cyan (défaut)', primary: '#0891b2', secondary: '#06b6d4', accent: '#0e7490' },
    { name: 'Bleu', primary: '#2563eb', secondary: '#3b82f6', accent: '#1d4ed8' },
    { name: 'Violet', primary: '#7c3aed', secondary: '#8b5cf6', accent: '#6d28d9' },
    { name: 'Rose', primary: '#db2777', secondary: '#ec4899', accent: '#be185d' },
    { name: 'Orange', primary: '#ea580c', secondary: '#f97316', accent: '#c2410c' },
    { name: 'Vert', primary: '#059669', secondary: '#10b981', accent: '#047857' }
  ];

  const applyPreset = (preset) => {
    setTheme({
      ...theme,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent
    });
    toast.success(`Palette "${preset.name}" appliquée !`);
  };

  return (
    <AdminLayout>
      <div className="p-8" data-testid="theme-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
              <Palette className="text-[#1e3a5f]" size={36} />
              Gestion du Thème
            </h1>
            <p className="text-gray-600 mt-2">
              Personnalisez l'apparence de votre application
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={togglePreview}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Eye size={18} />
              {previewMode ? 'Désactiver l\'aperçu' : 'Aperçu en direct'}
            </Button>
            <Button
              onClick={handleReset}
              variant="outline"
              className="flex items-center gap-2 text-orange-600 border-orange-600 hover:bg-orange-50"
              disabled={loading}
            >
              <RefreshCw size={18} />
              Réinitialiser
            </Button>
            <Button
              onClick={handleSave}
              className="flex items-center gap-2 bg-[#1e3a5f] hover:bg-[#162d4a]"
              disabled={loading}
            >
              <Save size={18} />
              {loading ? 'Sauvegarde...' : 'Enregistrer'}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Configuration des couleurs */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Palette de Couleurs</CardTitle>
                <CardDescription>
                  Définissez les couleurs principales de votre application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Color Inputs avec Validation HEX */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label>Couleur Primaire</Label>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={theme.primary_color}
                          onChange={(e) => handleChange('primary_color', e.target.value)}
                          className="w-20 h-10 p-1 cursor-pointer border-2 border-gray-300 rounded-lg"
                        />
                        <Input
                          type="text"
                          value={theme.primary_color}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                              handleChange('primary_color', value);
                            }
                          }}
                          placeholder="#0891b2"
                          className="font-mono"
                          maxLength={7}
                        />
                      </div>
                      <div
                        className="w-full h-16 rounded-lg border-2 relative overflow-hidden"
                        style={{ backgroundColor: theme.primary_color }}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white font-bold text-sm bg-black bg-opacity-50 px-3 py-1 rounded">
                            {theme.primary_color.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Couleur Secondaire</Label>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={theme.secondary_color}
                          onChange={(e) => handleChange('secondary_color', e.target.value)}
                          className="w-20 h-10 p-1 cursor-pointer border-2 border-gray-300 rounded-lg"
                        />
                        <Input
                          type="text"
                          value={theme.secondary_color}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                              handleChange('secondary_color', value);
                            }
                          }}
                          placeholder="#06b6d4"
                          className="font-mono"
                          maxLength={7}
                        />
                      </div>
                      <div
                        className="w-full h-16 rounded-lg border-2 relative overflow-hidden"
                        style={{ backgroundColor: theme.secondary_color }}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white font-bold text-sm bg-black bg-opacity-50 px-3 py-1 rounded">
                            {theme.secondary_color.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Couleur d'Accentuation</Label>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={theme.accent_color}
                          onChange={(e) => handleChange('accent_color', e.target.value)}
                          className="w-20 h-10 p-1 cursor-pointer border-2 border-gray-300 rounded-lg"
                        />
                        <Input
                          type="text"
                          value={theme.accent_color}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                              handleChange('accent_color', value);
                            }
                          }}
                          placeholder="#0e7490"
                          className="font-mono"
                          maxLength={7}
                        />
                      </div>
                      <div
                        className="w-full h-16 rounded-lg border-2 relative overflow-hidden"
                        style={{ backgroundColor: theme.accent_color }}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white font-bold text-sm bg-black bg-opacity-50 px-3 py-1 rounded">
                            {theme.accent_color.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Palettes prédéfinies */}
                <div>
                  <Label className="mb-3 block">Palettes Prédéfinies</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {colorPresets.map((preset, index) => (
                      <button
                        key={index}
                        onClick={() => applyPreset(preset)}
                        className="p-3 border-2 rounded-lg hover:border-gray-400 transition-all text-left"
                      >
                        <p className="text-sm font-semibold mb-2">{preset.name}</p>
                        <div className="flex gap-1">
                          <div
                            className="w-8 h-8 rounded"
                            style={{ backgroundColor: preset.primary }}
                          />
                          <div
                            className="w-8 h-8 rounded"
                            style={{ backgroundColor: preset.secondary }}
                          />
                          <div
                            className="w-8 h-8 rounded"
                            style={{ backgroundColor: preset.accent }}
                          />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Logos et Icônes</CardTitle>
                <CardDescription>
                  Gérez les logos et l'icône de votre application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Logo Standard (Fond Clair)</Label>
                    <label className="flex items-center gap-3 p-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 transition-colors">
                      <Upload size={16} className="text-gray-400" />
                      <span className="text-sm text-gray-500">Uploader le logo</span>
                      <input type="file" accept="image/*" className="hidden" data-testid="upload-logo" onChange={async (e) => {
                        if (!e.target.files[0]) return;
                        const fd = new FormData();
                        fd.append('file', e.target.files[0]);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await axios.post(`${API}/upload/cms-image`, fd, { headers: { Authorization: `Bearer ${token}` } });
                          handleChange('logo_url', res.data.url);
                          toast.success('Logo uploade');
                        } catch { toast.error("Erreur d'upload"); }
                      }} />
                    </label>
                    <div className="p-4 bg-white border-2 rounded-lg">
                      <img
                        src={theme.logo_url?.startsWith('/uploads') ? `${process.env.REACT_APP_BACKEND_URL}${theme.logo_url}` : theme.logo_url}
                        alt="Logo"
                        className="h-12 object-contain"
                        onError={(e) => {
                          e.target.src = '/SFOALC-logo.png';
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Logo Blanc (Fond Sombre)</Label>
                    <label className="flex items-center gap-3 p-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 transition-colors">
                      <Upload size={16} className="text-gray-400" />
                      <span className="text-sm text-gray-500">Uploader le logo blanc</span>
                      <input type="file" accept="image/*" className="hidden" data-testid="upload-logo-white" onChange={async (e) => {
                        if (!e.target.files[0]) return;
                        const fd = new FormData();
                        fd.append('file', e.target.files[0]);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await axios.post(`${API}/upload/cms-image`, fd, { headers: { Authorization: `Bearer ${token}` } });
                          handleChange('logo_white_url', res.data.url);
                          toast.success('Logo blanc uploade');
                        } catch { toast.error("Erreur d'upload"); }
                      }} />
                    </label>
                    <div className="p-4 bg-gray-900 border-2 rounded-lg">
                      <img
                        src={theme.logo_white_url?.startsWith('/uploads') ? `${process.env.REACT_APP_BACKEND_URL}${theme.logo_white_url}` : theme.logo_white_url}
                        alt="Logo blanc"
                        className="h-12 object-contain"
                        onError={(e) => {
                          e.target.src = '/SFOALC-blanc.png';
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Favicon</Label>
                  <label className="flex items-center gap-3 p-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 transition-colors">
                    <Upload size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-500">Uploader le favicon</span>
                    <input type="file" accept="image/*,.ico" className="hidden" data-testid="upload-favicon" onChange={async (e) => {
                      if (!e.target.files[0]) return;
                      const fd = new FormData();
                      fd.append('file', e.target.files[0]);
                      try {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${API}/upload/cms-image`, fd, { headers: { Authorization: `Bearer ${token}` } });
                        handleChange('favicon_url', res.data.url);
                        toast.success('Favicon uploade');
                      } catch { toast.error("Erreur d'upload"); }
                    }} />
                  </label>
                  <p className="text-xs text-gray-500">
                    Recommande : fichier .ico ou .png de 32x32 pixels
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Informations du Site</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom du Site</Label>
                  <Input
                    type="text"
                    value={theme.site_name}
                    onChange={(e) => handleChange('site_name', e.target.value)}
                    placeholder="SFOALC"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description du Site</Label>
                  <Input
                    type="text"
                    value={theme.site_description}
                    onChange={(e) => handleChange('site_description', e.target.value)}
                    placeholder="Description..."
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Panel */}
          <div className="space-y-6">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Aperçu</CardTitle>
                <CardDescription>
                  Visualisez vos modifications en temps réel
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Preview Button */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-3">Bouton Principal</p>
                  <button
                    className="px-6 py-2 rounded-lg text-white font-medium transition-colors"
                    style={{
                      backgroundColor: theme.primary_color
                    }}
                  >
                    Bouton d'Action
                  </button>
                </div>

                {/* Preview Card */}
                <div
                  className="p-4 rounded-lg border-l-4 bg-white"
                  style={{ borderLeftColor: theme.primary_color }}
                >
                  <h4 className="font-semibold mb-2">Carte avec Accent</h4>
                  <p className="text-sm text-gray-600">
                    Exemple de carte avec la couleur primaire en accent
                  </p>
                </div>

                {/* Preview Badge */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-3">Badge</p>
                  <span
                    className="px-3 py-1 rounded-full text-white text-sm"
                    style={{ backgroundColor: theme.secondary_color }}
                  >
                    Nouveau
                  </span>
                </div>

                {/* Preview Link */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-3">Lien</p>
                  <a
                    href="#"
                    className="font-medium underline"
                    style={{ color: theme.accent_color }}
                  >
                    Lien d'exemple
                  </a>
                </div>

                {/* Info Box */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    💡 <strong>Astuce :</strong> Activez l'aperçu en direct pour voir les changements sur toute la page
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ThemeManagementPage;
